(() => {
  const DISMISS_KEY = 'loomforma-discount-popup-dismissed';
  const SUBSCRIBED_KEY = 'loomforma-discount-popup-subscribed';

  const initDiscountPopup = () => {
    const modal = document.querySelector('[data-loomforma-discount-popup]');
    if (!modal || modal.dataset.popupInitialized === 'true') return;
    modal.dataset.popupInitialized = 'true';

    const body = document.body;
    const closeBtn = modal.querySelector('[data-popup-close]');
    const form =
      modal.querySelector('[data-popup-form]') ||
      modal.querySelector('.loomforma-subscribe-modal__form') ||
      modal.querySelector('form');
    const submitBtn = modal.querySelector('[data-popup-submit]');
    const submitLabel = modal.querySelector('[data-popup-submit-label]');
    const errorNode = modal.querySelector('[data-popup-status]');
    const isMobile = window.matchMedia('(max-width: 767px), (pointer: coarse)').matches;
    const popupDelay = Number(isMobile ? modal.dataset.popupDelayMobile : modal.dataset.popupDelayDesktop) ||
      (isMobile ? 15000 : 10000);
    const scrollThreshold = (Number(isMobile ? modal.dataset.popupScrollMobile : modal.dataset.popupScrollDesktop) ||
      (isMobile ? 50 : 40)) / 100;
    const dismissalDuration = (Number(modal.dataset.popupDismissDays) || 7) * 24 * 60 * 60 * 1000;
    const exitIntentEnabled = !isMobile && modal.dataset.popupExitIntent !== 'false' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const successLabel = modal.dataset.successLabel || 'Welcome To The Archive';
    const customerSubscribed = modal.dataset.customerSubscribed === 'true';
    const isDesignMode = window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode');

    const readStorage = (storage, key) => {
      try {
        return window[storage].getItem(key);
      } catch {
        return null;
      }
    };
    const writeStorage = (storage, key, value) => {
      try {
        window[storage].setItem(key, String(value));
      } catch {
        // In-memory state still prevents repeat popups when browser storage is blocked.
      }
    };
    let dismissedUntil = Number(readStorage('localStorage', DISMISS_KEY)) ||
      Number(readStorage('sessionStorage', DISMISS_KEY)) || 0;
    let subscribed = customerSubscribed || readStorage('localStorage', SUBSCRIBED_KEY) === 'true' ||
      readStorage('sessionStorage', SUBSCRIBED_KEY) === 'true';
    let showTimer = null;
    let scrollFrame = null;
    let triggerController = null;
    let overlayObserver = null;
    let pendingTrigger = false;
    let hasShown = false;
    const startedAt = Date.now();
    const lifecycleController = new AbortController();
    const { signal } = lifecycleController;

    const hasDismissed = () => Math.max(dismissedUntil, Number(readStorage('localStorage', DISMISS_KEY)) || 0) > Date.now();
    const hasSubscribed = () => subscribed || readStorage('localStorage', SUBSCRIBED_KEY) === 'true';
    const isSuppressed = () => isDesignMode || modal.dataset.popupHomepage !== 'true' || hasDismissed() || hasSubscribed();

    const stopTriggers = () => {
      window.clearTimeout(showTimer);
      window.cancelAnimationFrame(scrollFrame);
      showTimer = null;
      scrollFrame = null;
      pendingTrigger = false;
      triggerController?.abort();
      triggerController = null;
      overlayObserver?.disconnect();
      overlayObserver = null;
    };

    const setDismissed = () => {
      dismissedUntil = Date.now() + dismissalDuration;
      writeStorage('localStorage', DISMISS_KEY, dismissedUntil);
      writeStorage('sessionStorage', DISMISS_KEY, dismissedUntil);
      stopTriggers();
    };

    const setSubscribed = () => {
      subscribed = true;
      writeStorage('localStorage', SUBSCRIBED_KEY, 'true');
      writeStorage('sessionStorage', SUBSCRIBED_KEY, 'true');
      stopTriggers();
    };

    if (readStorage('sessionStorage', DISMISS_KEY) === 'true') {
      if (!dismissedUntil) dismissedUntil = Date.now() + dismissalDuration;
      writeStorage('localStorage', DISMISS_KEY, dismissedUntil);
      writeStorage('sessionStorage', DISMISS_KEY, dismissedUntil);
    }

    const responseState = modal.querySelector('[data-popup-response-state]')?.dataset.popupResponseState;
    const returnedFromSignup = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
    if (customerSubscribed || responseState === 'success' || (returnedFromSignup && responseState !== 'error')) {
      setSubscribed();
    }

    const setStatusMessage = (message, isError = false) => {
      if (!errorNode) return;
      errorNode.hidden = !message;
      errorNode.textContent = message || '';
      errorNode.classList.toggle('is-error', isError);
    };

    const parseResponseDocument = (html, url = '') => {
      const documentNode = new DOMParser().parseFromString(html, 'text/html');
      const stateNode = documentNode.querySelector('[data-popup-response-state]');

      if (stateNode) {
        const responseState = stateNode.dataset.popupResponseState;
        const responseMessage = stateNode.dataset.popupResponseMessage;

        if (responseState === 'success') {
          return { ok: true };
        }

        if (responseState === 'error') {
          return {
            ok: false,
            message: responseMessage || 'Enter a valid email address.',
          };
        }
      }

      if (url && new URL(url, window.location.href).searchParams.get('customer_posted') === 'true') {
        return { ok: true };
      }

      return { ok: false, message: 'Unable to subscribe right now.' };
    };

    const openModal = () => {
      if (hasShown || isSuppressed()) {
        stopTriggers();
        return;
      }
      if (!pendingTrigger || document.hidden) return;
      if (body.classList.contains('overflow-hidden') || body.classList.contains('loomforma-home-locked') ||
        document.querySelector('cart-drawer.active, [data-menu-overlay].active, [data-loomforma-mobile-nav].active, [data-loomforma-pro-overlay].active')) {
        if (!overlayObserver) {
          overlayObserver = new MutationObserver(openModal);
          overlayObserver.observe(body, { attributes: true, attributeFilter: ['class'], subtree: true });
        }
        return;
      }
      hasShown = true;
      stopTriggers();
      modal.classList.add('is-active');
      body.classList.add('loomforma-subscribe-modal-open');
    };

    const requestOpen = () => {
      pendingTrigger = true;
      openModal();
    };

    const checkScrollDepth = () => {
      scrollFrame = null;
      const scrollableHeight = Math.max(document.documentElement.scrollHeight, body.scrollHeight) - window.innerHeight;
      if (scrollableHeight > 0 && Math.max(0, window.scrollY) / scrollableHeight >= scrollThreshold) requestOpen();
    };

    const startTriggers = () => {
      if (hasShown || isSuppressed()) return;
      stopTriggers();
      triggerController = new AbortController();
      const triggerSignal = triggerController.signal;
      showTimer = window.setTimeout(requestOpen, Math.max(0, popupDelay - (Date.now() - startedAt)));
      window.addEventListener('scroll', () => {
        if (scrollFrame === null) scrollFrame = window.requestAnimationFrame(checkScrollDepth);
      }, { passive: true, signal: triggerSignal });
      document.addEventListener('visibilitychange', openModal, { signal: triggerSignal });
      if (exitIntentEnabled) {
        document.addEventListener('mouseout', (event) => {
          if (event.relatedTarget === null && event.clientY <= 0) requestOpen();
        }, { signal: triggerSignal });
      }
      checkScrollDepth();
    };

    const closeModal = ({ persistDismiss = true } = {}) => {
      modal.classList.remove('is-active');
      body.classList.remove('loomforma-subscribe-modal-open');

      if (persistDismiss) {
        setDismissed();
      }
    };

    startTriggers();

    window.addEventListener('pagehide', stopTriggers, { signal });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) startTriggers();
    }, { signal });
    window.addEventListener('storage', (event) => {
      if (event.key !== DISMISS_KEY && event.key !== SUBSCRIBED_KEY) return;
      if (hasDismissed() || hasSubscribed()) {
        stopTriggers();
        closeModal({ persistDismiss: false });
      }
    }, { signal });
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(modal)) return;
      stopTriggers();
      closeModal({ persistDismiss: false });
      lifecycleController.abort();
    }, { signal });

    if (closeBtn) {
      closeBtn.addEventListener('click', () => closeModal(), { signal });
    }

    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        closeModal();
      }
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-active')) {
        closeModal();
      }
    }, { signal });

    if (!form || !submitBtn || !submitLabel) return;

    const resetFormState = () => {
      submitBtn.disabled = false;
      submitBtn.classList.remove('is-success');
    };

    const handleSuccess = () => {
      submitLabel.textContent = successLabel;
      submitBtn.classList.add('is-success');
      setSubscribed();

      window.setTimeout(() => {
        closeModal({ persistDismiss: false });
        window.setTimeout(() => {
          submitLabel.textContent = modal.dataset.defaultLabel || submitLabel.textContent;
          resetFormState();
          form.reset();
          setStatusMessage('', false);
        }, 400);
      }, 1500);
    };

    modal.dataset.defaultLabel = submitLabel.textContent;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      try {
        setStatusMessage('', false);
        submitBtn.disabled = true;

        const response = await fetch(form.action || window.location.pathname, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) throw new Error('Unable to subscribe right now. Please try again.');
        const responseText = await response.text();
        const result = parseResponseDocument(responseText, response.url);

        if (!result.ok) {
          throw new Error(result.message || 'Unable to subscribe right now.');
        }

        handleSuccess();
      } catch (error) {
        resetFormState();
        setStatusMessage(error.message || 'Unable to subscribe right now.', true);
      }
    }, { signal });

  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDiscountPopup);
  } else {
    initDiscountPopup();
  }
})();
